// window.atlas.action.* — cross-app Actions over the postMessage protocol.
//
// Spec: info-data/Harmony_Actions.md + info-data/Cross-Domain Harmony Actions.md.
// Ported message types to the atlas namespace:
//   parent -> action iframe:  { type: "atlas.action.init", executionState: { input } }
//   action iframe -> parent:  { type: "atlas.action.message", data }            (finish)
//                             { type: "atlas.action.message", abort: true }      (cancel)
//
// Two roles, both provided by the SDK so the same code path works regardless of where the action
// is hosted (native or cross-domain):
//   - Consumer:  atlas.action.run(name, input) -> Promise<result>. Inserts an iframe whose src is
//     the action's page, sends init, resolves on the finish message (rejects on abort).
//   - Vendor (the action page): isAction(), getState() -> input, finishWithResult(output), abort().
//
// The DOM/window are injected (doc, parentWindow, listen) so the protocol is unit-testable.

import type { ActionApi, ActionDeps, ActionFilters, ActionInfo, RoutingMode } from "./types";

export const INIT_TYPE = "atlas.action.init";
export const MESSAGE_TYPE = "atlas.action.message";

// Resolve an action name ("app/action") to its hosted action-page URL on the platform.
//
// Routing-mode aware (opts default to path mode, preserving the original contract):
//   - path mode:      <appOrigin>/<app>/<action>.html   (the app lives under a path segment)
//   - subdomain mode: https://<app>.<appDomain>/<action>.html
// Subdomain mode MUST address the action's app by its own subdomain, NOT append "/<app>/" to the
// current origin — on an app subdomain the viewer-request rewrite already prepends "/<app>", so
// "<app>.<domain>/<app>/x" would double-prefix to "/<app>/<app>/x" and 404. Building the subdomain
// URL makes the rewrite map "<app>.<domain>/<action>.html" -> "/<app>/<action>.html" correctly, and
// also lets a page on ANY subdomain invoke another app's action.
export function actionSrc(
  appOrigin: string,
  name: string,
  { routingMode, appDomain }: { routingMode?: RoutingMode; appDomain?: string } = {},
): string {
  const [app, action] = name.split("/");
  if (!app || !action) throw new Error(`invalid action name: ${name}`);
  if (routingMode === "subdomain" && appDomain) {
    return `https://${app}.${appDomain}/${action}.html`;
  }
  return `${appOrigin}/${app}/${action}.html`;
}

export function makeActionApi(deps: ActionDeps): ActionApi {
  const consumer = makeConsumer(deps);
  const vendor = makeVendor(deps);
  return { ...consumer, ...vendor };
}

// --- consumer side (the app invoking an action) ---

function makeConsumer({ doc, addMessageListener, appOrigin, routingMode, appDomain, api }: ActionDeps) {
  return {
    // Run an action in an isolated iframe; resolve with its result, reject if the user aborts.
    run(name: string, input?: unknown): Promise<unknown> {
      return new Promise((resolve, reject) => {
        const src = actionSrc(appOrigin, name, { routingMode, appDomain });
        const frame = doc.createElement("iframe");
        frame.src = src;
        frame.setAttribute("data-atlas-action", name);

        const onMessage = (event: { data: import("./types").ActionMessage; source?: unknown }) => {
          const d = event.data;
          if (!d || d.type !== MESSAGE_TYPE) return;
          if (event.source && frame.contentWindow && event.source !== frame.contentWindow) return;
          cleanup();
          if (d.abort) reject(new Error("action aborted"));
          else resolve(d.data);
        };

        const onLoad = () => {
          frame.contentWindow.postMessage(
            { type: INIT_TYPE, executionState: { input } },
            "*", // a stricter target origin can be set once the action's origin is known
          );
        };

        const removeListener = addMessageListener(onMessage);
        function cleanup() {
          removeListener();
          frame.removeEventListener("load", onLoad);
          if (frame.parentNode) frame.parentNode.removeChild(frame);
        }

        frame.addEventListener("load", onLoad);
        doc.body.appendChild(frame);
      });
    },

    // search(filters) discovers actions vended across all live apps via the control-plane catalog
    // (GET /actions), then filters client-side. Returns [] if no API requester was wired (e.g. a
    // vendor-only page that never got an authed client). Filters (all optional): `app` (exact match)
    // and `text` (case-insensitive substring over name/displayName/description).
    async search(filters: ActionFilters = {}): Promise<ActionInfo[]> {
      if (!api) return [];
      const res = await api.request("GET", "/actions");
      const all: ActionInfo[] = (res && res.actions) || [];
      const text = (filters.text || "").trim().toLowerCase();
      return all.filter((a) => {
        if (filters.app && a.app !== filters.app) return false;
        if (text) {
          const haystack = `${a.name} ${a.displayName} ${a.description}`.toLowerCase();
          if (!haystack.includes(text)) return false;
        }
        return true;
      });
    },
  };
}

// --- vendor side (the action implementation page) ---

function makeVendor({ parentWindow, getInitMessage }: ActionDeps) {
  // getInitMessage() returns the cached init payload received from the parent (or null).
  return {
    isAction() {
      return Boolean(getInitMessage());
    },

    // Input passed by the calling app.
    getState() {
      const init = getInitMessage();
      return init ? init.executionState?.input : undefined;
    },

    // Return output to the caller and signal completion (the parent removes the iframe).
    finishWithResult(output: unknown) {
      parentWindow.postMessage({ type: MESSAGE_TYPE, data: output }, "*");
    },

    // Cancel without returning output.
    abort() {
      parentWindow.postMessage({ type: MESSAGE_TYPE, abort: true }, "*");
    },
  };
}
